
import { Component, Prop, Watch, Vue } from 'vue-property-decorator'
import { last } from 'lodash'
import {
  createUUID,
  getDownloadURL,
  getDownloadURLWithToken,
  downloadFileByCodeWithToken,
  isAccpetFile,
} from '@/utils/helper'
import config from '@/config'
import pinia from '@/store'
import { useTokenStore } from '@/store/token'

const tokenStore = useTokenStore(pinia)

export interface UploadInfo {
  file: any
  fileList: any[]
  event: any
}

@Component
export default class Uploader extends Vue {
  @Prop({
    type: Boolean,
    default: false,
  })
  private disabled!: boolean

  @Prop({
    type: Boolean,
    default: false,
  })
  private isUnique!: boolean

  @Prop({
    type: Boolean,
    default: false,
  })
  private multiple!: boolean

  @Prop({
    type: Array,
    default: () => [],
  })
  private fileList!: any[]

  @Prop({
    type: String,
    default: 'text',
  })
  private listType!: string

  @Prop({
    type: [Boolean, Object],
    default: () => true,
  })
  private showUploadList!: boolean | { showPreviewIcon?: boolean; showRemoveIcon?: boolean }

  @Prop({
    type: Boolean,
    default: true,
  })
  private showAction!: boolean

  @Prop({
    type: String,
    default: '',
  })
  private url!: string
  @Prop({
    type: String,
    default: () => '*',
  })
  private accept!: string
  @Prop({
    type: Object,
    default: () => ({}),
  })
  private data!: object
  @Prop({
    type: Number,
  })
  private maxSize
  @Prop({
    type: Number,
  })
  private minSize

  @Watch('fileList', {
    immediate: true,
    deep: true,
  })
  private onFileListChange(values: any[]) {
    this.updateFiles(values)
  }

  public updateFiles(values: any[] = []) {
    this.files =
      values.map((item, index) => {
        return {
          ...item,
          uid: index,
          name: item.fileName,
          status: 'done',
          url: item.fileCode ? getDownloadURL(item.fileCode) : item.fileUrl,
          type: item.type,
        }
      }) || []
  }

  private get uploadListMode() {
    if (this.disabled) {
      return {
        showRemoveIcon: false,
      }
    } else {
      return this.showUploadList
    }
  }

  private action: string = config.get('SERVER_URL') + 'api/file/upload'

  private headers = {
    Authorization: `Bearer ${tokenStore.getToken()}`,
  }

  private files: any[] = []

  private created() {
    this.action = this.url ? config.get('SERVER_URL') + this.url : config.get('SERVER_URL') + 'api/file/upload'
  }

  private beforeUpload(file, fileList) {
    if (this.accept !== '*' && !isAccpetFile(this.accept, fileList)) {
      this.$message.error('您选择的文件有不符合格式要求的文件，请重新选择')
      throw Error('您选择的文件有不符合格式要求的文件，请重新选择')
    }
    if (this.maxSize || this.maxSize) {
      if (file.size > this.maxSize) {
        this.$message.warning(`文件大小不能超过${this.getSize(this.maxSize)}`)
        throw Error(`文件大小不能超过${this.getSize(this.maxSize)}`)
      } else if (file.size < this.minSize) {
        this.$message.warning(`文件大小不能小于${this.getSize(this.minSize)}`)
        throw Error(`文件大小不能小于${this.getSize(this.minSize)}`)
      }
    }
    this.$emit('beforeUpload', file, fileList)
  }
  /** @param {number} value 字节(Byte) */
  private getSize(value) {
    return value >= 1024 * 1024 * 1024
      ? `${value / 1024 / 1024 / 1024}G`
      : value >= 1024 * 1024
      ? `${value / 1024 / 1024}M`
      : `${value / 1024}KB`
  }
  private onChange(info: UploadInfo) {
    let fileList = this.isUnique ? (info.fileList.length ? [last(info.fileList)] : []) : [...info.fileList]
    fileList = fileList.map((file) => {
      return file
    })
    this.files = fileList
    let attachments = this.files.map((item) => {
      if (item.response) {
        const { data, code } = item.response
        if (code === 0) {
          return {
            fileName: data[0].fileName,
            url: getDownloadURL(data[0].code),
            code: data[0].code,
            fileCode: data[0].code,
            uid: item.uid || data[0].id || createUUID(),
            type: item.type,
            size: item.size,
            uploadTime: data[0].uploadTime,
            fileExt: data[0].fileExt,
          }
        } else {
          return false
        }
      }
      return item
    })
    if (info.file.status === 'done' && info.file.response.code === 0) {
      const { data } = info.file.response
      if (data) {
        const file = {
          fileName: data[0].fileName,
          url: getDownloadURL(data[0].code),
          code: data[0].code,
          fileCode: data[0].code,
          uid: info.file.uid || data[0].id || createUUID(),
          type: info.file.type,
          size: info.file.size,
          uploadTime: data[0].uploadTime,
          fileExt: data[0].fileExt,
        }
        this.$emit('success', file, info)
      }
    } else if (info.file.status === 'error' || (info.file.status === 'done' && info.file.response.code !== 0)) {
      this.$emit('error', info)
      this.$message.warning(info.file.response.msg)
      return false
    }
    if (fileList.every((f) => f.status !== 'uploading' && f.status !== 'error')) {
      attachments = attachments.filter((v) => v !== false)
      const files = this.isUnique ? (attachments.length ? [last(attachments)] : []) : attachments
      this.$emit('change', files, info)
    }
  }

  private async onPreview(file) {
    const data = {
      fileName: file.fileName,
      code: file.fileCode,
      fileCode: file.fileCode,
      url: file.fileUrl,
      type: file.fileType,
    }

    // 通过 getDownloadURLWithToken 获取地址，Token 不存在/已过期/临近过期会自动刷新
    const url = file.fileCode ? await getDownloadURLWithToken(file.fileCode) : file.fileUrl
    const ext = file.fileName?.split('.').pop()?.toLowerCase() || ''
    const SUPPORT_PREVIEW_IMAGE_EXT_TYPE = ['jpg', 'jpeg', 'png', 'bmp', 'gif']

    if ((file.type && file.type?.includes('image')) || SUPPORT_PREVIEW_IMAGE_EXT_TYPE.includes(ext)) {
      this.$privewImg([{ fileName: file.fileName || file.name || '', url }])
    } else {
      downloadFileByCodeWithToken(data)
    }
  }
}
